import React, { useCallback, useEffect, useState } from 'react'

type UseBreakPointsProps = {
  xs?: number
  sm?: number
  md?: number
  lg?: number
  xl?: number
  xxl?: number
}

/**
 * As in AntD documentation https://ant.design/components/grid/
 * xs < 576px
 * sm >= 576px
 * md >= 768px
 * lg >= 992px
 * xl >= 1200px
 * xll >= 1600px
 */

type BreakPoints = {
  xs: boolean
  sm: boolean
  md: boolean
  lg: boolean
  xl: boolean
  xxl: boolean
}

function useBreakPoints(props?: UseBreakPointsProps) {
  const [breakpoints, setBreakpoints] = useState<BreakPoints>({
    xs: false,
    sm: false,
    md: false,
    lg: false,
    xl: false,
    xxl: false,
  })

  function debounce(func, timeout = 300) {
    let timer
    return (...args) => {
      clearTimeout(timer)
      timer = setTimeout(() => {
        func.apply(this, args)
      }, timeout)
    }
  }

  const handleResize = debounce(
    useCallback(() => {
      if (window.innerWidth >= (props?.xxl || 1600)) {
        setBreakpoints({
          xs: false,
          sm: false,
          md: false,
          lg: false,
          xl: false,
          xxl: true,
        })
      } else if (window.innerWidth >= (props?.xl || 1200)) {
        setBreakpoints({
          xs: false,
          sm: false,
          md: false,
          lg: false,
          xl: true,
          xxl: false,
        })
      } else if (window.innerWidth >= (props?.lg || 992)) {
        setBreakpoints({
          xs: false,
          sm: false,
          md: false,
          lg: true,
          xl: false,
          xxl: false,
        })
      } else if (window.innerWidth >= (props?.md || 768)) {
        setBreakpoints({
          xs: false,
          sm: false,
          md: true,
          lg: false,
          xl: false,
          xxl: false,
        })
      } else if (window.innerWidth >= (props?.sm || 576)) {
        setBreakpoints({
          xs: false,
          sm: true,
          md: false,
          lg: false,
          xl: false,
          xxl: false,
        })
      } else if (window.innerWidth < (props?.xs || 576)) {
        setBreakpoints({
          xs: true,
          sm: false,
          md: false,
          lg: false,
          xl: false,
          xxl: false,
        })
      }
    }, [setBreakpoints]),
  )

  useEffect(() => {
    handleResize()
    window.addEventListener('resize', handleResize, true)
    return () => {
      window.removeEventListener('resize', handleResize, true)
    }
  }, [])

  return {
    // breakpoints,
    isXs: breakpoints.xs,
    isSm: breakpoints.sm || breakpoints.md || breakpoints.lg || breakpoints.xl || breakpoints.xxl,
    isMd: breakpoints.md || breakpoints.lg || breakpoints.xl || breakpoints.xxl,
    isLg: breakpoints.lg || breakpoints.xl || breakpoints.xxl,
    isXl: breakpoints.xl || breakpoints.xxl,
    isXxl: breakpoints.xxl,
  }
}

export default useBreakPoints
